import { cn } from "@newxsystems/shared";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootMediaFrame } from "../../../../modules/surface/components/TootMediaFrame/TootMediaFrame";
import type { IImageGridBlockProps } from "./ImageGridBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

export const ImageGridBlock = (props: IImageGridBlockProps) => {
  const {
    anchorId,
    buttons,
    eyebrow,
    heading,
    items = [],
    parentClass,
    spacing,
    subheading,
  } = props;
  const usesTwoColumns = items.length === 2;
  const usesFourColumns = items.length > 0 && items.length % 4 === 0;

  if (!items.length) {
    return null;
  }

  const sectionId = anchorId ?? undefined;
  const alignment = "left" as const;
  const actions = buttons?.length ? (
    <TootActions actions={buttons} alignment={alignment} />
  ) : undefined;
  const header = heading ? (
    <TootHeading
      alignment={alignment}
      eyebrow={eyebrow ?? undefined}
      heading={heading}
      subheading={subheading ?? undefined}
    />
  ) : eyebrow || subheading ? (
    <div className="flex max-w-3xl flex-col items-start gap-4 text-left">
      {eyebrow ? <p className="toot-eyebrow">{eyebrow}</p> : null}
      {subheading ? <RichTextHtml className="toot-subheading" html={subheading} /> : null}
    </div>
  ) : undefined;

  return (
    <TootSectionShell
      actions={actions}
      className="relative"
      containerClassName="flex flex-col"
      header={header}
      id={sectionId}
      rootClassName={parentClass}
      spacing={spacing ?? undefined}
      tone="default"
      width="wide"
    >
      <div className="relative" data-toot-image-grid="contact-sheet">
        <div
          className={cn(
            "grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:gap-5",
            usesTwoColumns
              ? "lg:grid-cols-2"
              : usesFourColumns
                ? "lg:grid-cols-4"
                : "lg:grid-cols-3",
          )}
        >
          {items.map((item, index) => {
            const { image } = item;

            return (
              <article
                className="toot-heat-panel group/image-grid relative flex h-full min-w-0 flex-col rounded-[var(--radius-toot-frame)] transition-[border-color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:border-toot-orange/60 hover:shadow-2xl hover:shadow-black/50 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                key={`${image.defaultSrc}-${index}`}
              >
                <div className="relative z-10 overflow-hidden border-b border-toot-grid-strong/70">
                  <TootMediaFrame
                    alt={image.alternateText}
                    aspectRatio={item.aspectRatio ?? "4 / 3"}
                    className="rounded-none border-0 bg-[var(--color-toot-black)] [&>div]:min-h-[16rem] [&_img]:contrast-[1.18] [&_img]:saturate-[0.82] [&_img]:brightness-[0.74] [&_img]:transition-transform [&_img]:duration-500 [&_img]:ease-out group-hover/image-grid:[&_img]:scale-[1.035] motion-reduce:[&_img]:transition-none motion-reduce:group-hover/image-grid:[&_img]:scale-100"
                    focalPoint={image.focalPoint ?? undefined}
                    height={item.height ?? image.height ?? undefined}
                    loading={index < 3 ? "eager" : "lazy"}
                    sizes={
                      image.sizes ??
                      (usesTwoColumns
                        ? "(min-width: 1024px) 46vw, (min-width: 768px) 45vw, 100vw"
                        : usesFourColumns
                          ? "(min-width: 1024px) 22vw, (min-width: 768px) 45vw, 100vw"
                          : "(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 100vw")
                    }
                    src={image.defaultSrc}
                    srcSet={image.srcSet ?? undefined}
                    width={item.width ?? image.width ?? undefined}
                  />
                </div>
                {item.category ? (
                  <div className="relative z-10 flex min-h-11 items-center border-b border-[color-mix(in_oklab,var(--color-toot-grid-strong)_74%,transparent)] px-3 py-2">
                    <span className="min-w-0 truncate text-[0.62rem] font-black uppercase leading-none tracking-[0.18em] text-[color-mix(in_oklab,var(--color-toot-foreground)_62%,transparent)]">
                      {item.category}
                    </span>
                  </div>
                ) : null}
                <div className="relative z-10 flex flex-1 flex-col gap-3 p-4 sm:p-5">
                  {item.caption || item.credit ? (
                    <div className="flex min-w-0 flex-col gap-2">
                      {item.caption ? (
                        <p className="text-pretty text-base font-black leading-6 tracking-[-0.018em] text-[var(--color-toot-foreground)] [overflow-wrap:anywhere]">
                          {item.caption}
                        </p>
                      ) : null}
                      {item.credit ? (
                        <p className="flex flex-wrap gap-x-3 gap-y-1 text-[0.68rem] font-black uppercase leading-5 tracking-[0.16em] text-[color-mix(in_oklab,var(--color-toot-orange)_74%,var(--color-toot-foreground))]">
                          {item.credit}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  {item.tags?.length ? (
                    <ul
                      aria-label="Image tags"
                      className="mt-auto flex list-none flex-wrap gap-1.5 p-0"
                    >
                      {item.tags.map((tag, tagIndex) => (
                        <li
                          className="rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_76%,transparent)] bg-toot-surface-raised/60 px-2.5 py-1 text-xs font-semibold leading-none text-[color-mix(in_oklab,var(--color-toot-muted)_88%,var(--color-toot-foreground))]"
                          key={`${tag.tag}-${tagIndex}`}
                        >
                          {tag.tag}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </TootSectionShell>
  );
};

ImageGridBlock.displayName = "Block::ImageGridBlock";
